import { Box, styled, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useGetMooringsWithComponents } from 'src/api/components'
import { AppTextField } from 'src/components/AppTextField'
import BasicDatePicker from 'src/components/DateTimePicker'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { CenteredSpinner } from 'src/components/Spinner'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { IComponentFilters } from 'src/generated-types'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getMooringName, getUTCDate } from 'src/utils/function'
import { getComponentOverviewHeaderKeyList } from './ComponentOverviewHelper'
import LocationDetailAccordion from './LocationDetailAccordion'

interface IComponentOverviewTableContainerProps {
  customerId: EntityId
  filters: IComponentFilters
  setFilters: React.Dispatch<React.SetStateAction<IComponentFilters>>
}
const ComponentOverviewTableContainer: React.FC<IComponentOverviewTableContainerProps> = ({
  customerId,
  filters,
  setFilters,
}) => {
  const { t } = useTranslation(['component-overview'])
  const theme = useTheme()
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const boxBorder = `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`
  const { data: mooringsWithComponents, isLoading } = useGetMooringsWithComponents(customerId, filters)

  return (
    <Box>
      <Box paddingLeft={1} flexDirection={'column'} gap={1}>
        <Box
          height={'90px'}
          display={'flex'}
          flexDirection={'row'}
          gap={1}
          border={boxBorder}
          position={'sticky'}
          top={0}
          bgcolor={getThemeColor(theme, MUIThemeColors.white)}
          paddingRight={2}
        >
          {!isMobile ? (
            <Box width={44} height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'} />
          ) : (
            <></>
          )}
          <Box
            paddingBottom={'20px'}
            paddingTop={'12px'}
            display={'flex'}
            flexDirection={'row'}
            flex={1}
            minWidth={0}
            gap={1}
            width={'100%'}
            overflow={'hidden'}
          >
            {getComponentOverviewHeaderKeyList(t, isMobile).map(
              (item: { width?: number; headerName: string; type: string; name: string; flex?: number }, index) => (
                <Box
                  key={index}
                  width={item?.width}
                  flex={item?.flex}
                  alignItems={'flex-start'}
                  display={'flex'}
                  flexDirection={'column'}
                  gap={1}
                  paddingBottom={2}
                  minWidth={0}
                >
                  <DynamicTextTypography
                    text={item.headerName}
                    sx={{ paddingLeft: '2px', fontSize: isMobile ? '9px' : '16px' }}
                    maxLength={15}
                  />

                  {item.type !== 'date' ? (
                    <AppTextField
                      name={item.name}
                      type={item.name == 'quantity' || item.name == 'lifespanLeft' ? 'number' : 'text'}
                      sx={{ padding: '0', width: '100%' }}
                      placeholder={t('search', { ns: 'common' })}
                      size="small"
                      value={filters[item.name] ?? ''}
                      onChange={e => setFilters(prev => ({ ...prev, [item.name]: e.target.value }))}
                    />
                  ) : (
                    <StyledDatePickerWrapper alignContent={'center'}>
                      <BasicDatePicker
                        name={item.name}
                        isWithoutAppForm
                        value={filters[item.name]}
                        onChange={date =>
                          setFilters(prev => ({
                            ...prev,
                            [item.name]: date ? (getUTCDate(new Date(date)) as Date) : null,
                          }))
                        }
                      />
                    </StyledDatePickerWrapper>
                  )}
                </Box>
              ),
            )}
          </Box>
        </Box>
      </Box>
      <Box paddingLeft={1} maxHeight={!isLoading ? 'calc(100vh - 410px)' : 'auto'} overflow={'auto'}>
        {isLoading ? (
          <CenteredSpinner size={'50px'} height={300} />
        ) : mooringsWithComponents && mooringsWithComponents?.length > 0 ? (
          mooringsWithComponents.map(mooring => (
            <LocationDetailAccordion
              key={mooring.mooringId}
              localityName={mooring.localityName ?? ''}
              mooringName={getMooringName(mooring.mooringName ?? '')}
              customerId={customerId}
              localityId={mooring.localityId}
              mooringId={mooring.mooringId}
              filters={filters}
            />
          ))
        ) : (
          <EmptyStatusCard
            imageWidth="125px"
            height={'400px'}
            content={t('component-overview-empty-status-content', { ns: 'component' })}
            header={t('component-overview-empty-status-title', { ns: 'component' })}
          />
        )}
      </Box>
    </Box>
  )
}

export default ComponentOverviewTableContainer

const StyledDatePickerWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  paddingTop: '0px !important',
  '& .MuiTextField-root': {
    padding: 0,
    margin: 0,
  },
  '& .MuiInputBase-root': {
    height: '40px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '8.5px 14px',
  },
  '& .MuiFormHelperText-root': {
    margin: 0,
  },
  '& .MuiBox-root': {
    padding: '0px !important',
  },
})
